import { useState } from 'react';
import { useMutation } from 'react-apollo';

import {
  marketingMaterialAssetCreate as marketingMaterialAssetCreateMutation,
  marketingMaterialAssetUpdate as marketingMaterialAssetUpdateMutation,
} from '../../../../../../../graphql/mutations/marketing-material-assets';
import useOpenAlert from '../../../../../../../hooks/useOpenAlert';
import { getErrorString } from '../../../../../../../util/normalize-errors';

/**
 * Returns the albumFolder's custom design join records of a given
 * marketing material type, grouped by their MarketingMaterialAsset id.
 *
 * @param {Object} albumFolder The albumFolder with marketing_material_assets.
 * @param {String} marketingMaterialType The marketing material type.
 * @returns {Object} Join record ids keyed by MarketingMaterialAsset id.
 */
export const getFolderJoinIdsByAssetId = (
  albumFolder,
  marketingMaterialType,
) => {
  const joinIdsByAssetId = {};

  (albumFolder?.marketing_material_assets?.records ?? [])
    .filter(
      record =>
        record?.marketing_material_asset?.marketing_material_type ===
        marketingMaterialType,
    )
    .forEach(record => {
      const assetId = record.marketing_material_asset.id;

      joinIdsByAssetId[assetId] = [
        ...(joinIdsByAssetId[assetId] ?? []),
        record.id,
      ];
    });

  return joinIdsByAssetId;
};

/**
 * Saves a custom marketing material design for an albumFolder: creates the
 * MarketingMaterialAsset when a new upload is provided (or reuses an existing
 * one), links it to the folder and unlinks any other design of the same type.
 *
 * Note: the backend requires unlink (delete) ids to belong to the asset being
 * updated, so linking the new asset and unlinking replaced ones are separate
 * mutation calls. The new link is created first — folder records are ordered
 * created_at DESC, so the newest link wins in display even mid-flight.
 *
 * @param {Object} root0 The hook params.
 * @param {Object} root0.albumFolder The albumFolder with marketing_material_assets.
 * @param {String} root0.marketingMaterialType The marketing material type.
 * @returns {{onSave: Function, onRemove: Function, submitting: boolean}}
 */
const useMarketingMaterialAssetSave = ({
  albumFolder,
  marketingMaterialType,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [marketingMaterialAssetCreate] = useMutation(
    marketingMaterialAssetCreateMutation,
  );
  const [marketingMaterialAssetUpdate] = useMutation(
    marketingMaterialAssetUpdateMutation,
  );
  const openAlert = useOpenAlert();

  /**
   * Unlinks the given join records, one update call per owning asset.
   *
   * @param {Object} joinIdsByAssetId Join ids keyed by MarketingMaterialAsset id.
   */
  const unlinkJoins = async joinIdsByAssetId => {
    const assetIds = Object.keys(joinIdsByAssetId);

    for (let i = 0; i < assetIds.length; i += 1) {
      await marketingMaterialAssetUpdate({
        variables: {
          input: {
            marketing_material_asset_id: assetIds[i],
            album_folder_marketing_material_assets: {
              delete_album_folder_marketing_material_asset_ids:
                joinIdsByAssetId[assetIds[i]],
            },
          },
        },
      });
    }
  };

  const onSave = async ({ assetImageId, assetName, existingAssetId }) => {
    setSubmitting(true);

    try {
      let assetId = existingAssetId;

      if (!assetId) {
        const response = await marketingMaterialAssetCreate({
          variables: {
            input: {
              marketing_material_type: marketingMaterialType,
              name: assetName,
              asset_image: {
                asset_image_id: assetImageId,
              },
            },
          },
        });

        assetId =
          response?.data?.marketingMaterialAssetCreate?.marketing_material_asset
            ?.id;

        if (!assetId) {
          throw new Error('The custom design could not be created');
        }
      }

      const joinIdsByAssetId = getFolderJoinIdsByAssetId(
        albumFolder,
        marketingMaterialType,
      );
      const { [assetId]: currentJoinIds, ...staleJoinIdsByAssetId } =
        joinIdsByAssetId;

      if (!currentJoinIds?.length) {
        await marketingMaterialAssetUpdate({
          variables: {
            input: {
              marketing_material_asset_id: assetId,
              album_folder_marketing_material_assets: {
                create_album_folder_marketing_material_assets: [
                  { album_folder_id: albumFolder.id },
                ],
              },
            },
          },
        });
      }

      await unlinkJoins(staleJoinIdsByAssetId);

      return { success: true, marketingMaterialAssetId: assetId };
    } catch (error) {
      openAlert({
        message: 'There was an error while saving your custom design',
        info: getErrorString(error),
      });

      return { success: false };
    } finally {
      setSubmitting(false);
    }
  };

  const onRemove = async () => {
    setSubmitting(true);

    try {
      await unlinkJoins(
        getFolderJoinIdsByAssetId(albumFolder, marketingMaterialType),
      );

      return { success: true };
    } catch (error) {
      openAlert({
        message: 'There was an error while removing your custom design',
        info: getErrorString(error),
      });

      return { success: false };
    } finally {
      setSubmitting(false);
    }
  };

  return { onSave, onRemove, submitting };
};

export default useMarketingMaterialAssetSave;
